import { Divider } from "@mantine/core";
import { type CSSProperties, useMemo, useRef } from "react";
import Section from "src/components/shared/EntriesGenerator/Section/Section/Section";
import { Select } from "src/components/shared/MantineCombobox";
import { useTheme } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSnapshotPageSize } from "src/redux/slices/generatorSlice";
import { Flex } from "src/styled/shared.styled";

import classes from "./SnapshotDisplay.module.scss";

const SnapshotDisplay = () => {
  const resultsRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { colors } = useTheme();

  const { snapshotHeaders, snapshotEntry, snapshotPageSize } = useSelector(
    state => state.generatorSlice,
  );

  // Create a section object for the Section component (always call useMemo)
  const section = useMemo(() => {
    if (
      !snapshotEntry ||
      !snapshotHeaders ||
      !Array.isArray(snapshotEntry.list) ||
      snapshotEntry.list.length === 0
    ) {
      return null;
    }
    return {
      headers: snapshotHeaders,
      rows: snapshotEntry.list,
      title: "",
      colcount: 0,
      className: "passive readonly-snapshot",
    };
  }, [snapshotEntry, snapshotHeaders]);

  // If no snapshot data, don't render anything
  if (!section || !snapshotHeaders || !snapshotEntry) {
    return null;
  }

  return (
    <Flex
      mt={80}
      direction="column"
      className={classes.root}
      style={
        {
          "--snapshot-text-color": colors.grayscaleText[0],
          "--snapshot-border-color": colors.grayscale[0],
          "--snapshot-head-bg-color": colors.grayscale[1],
          "--snapshot-cell-border-color": colors.grayscale[1],
          "--snapshot-cell-bg-color": colors.grayscale[2],
          "--snapshot-divider-color": colors.grayscaleBorder[0],
        } as CSSProperties
      }>
      <Flex justify="space-between" align="flex-start" mb={10}>
        <Flex direction="column" gap={15}>
          <div className={classes.title}>
            Historical Snapshot - {snapshotEntry.lbname ?? ""}
          </div>
          <div className={classes.subtitle}>
            This is a read-only view of historical snapshot data
          </div>
        </Flex>
        <Flex gap={20} align="center">
          <div className={classes.entriesLabel}>Number of entries:</div>
          <Select
            maw={130}
            value={String(snapshotPageSize)}
            allowDeselect={false}
            data={["25", "50", "100", "250"]}
            onChange={value => {
              const nextSize = parseInt(value ?? "25");
              if (Number.isFinite(nextSize) && nextSize > 0) {
                dispatch(setSnapshotPageSize(nextSize));
              }
            }}
          />
        </Flex>
      </Flex>

      <Divider my={12} color="var(--snapshot-divider-color)" />
      <div className={classes.tableContainer}>
        {section && (
          <Section
            readOnly
            section={section}
            historyRows={[]}
            sIndex={0}
            snapshot
            horizontalScroll
            ref={resultsRef}
          />
        )}
      </div>
    </Flex>
  );
};

export default SnapshotDisplay;
